import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useTaulukko } from "./taulukko";
import { Ohje } from "./Ohje";
import { api, ApiError, type Kayttaja, type Materiaali, type MateriaaliTuonti } from "./api";
import { t, esim, virheTeksti, type KaannosAvain } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Materiaalikatalogi.

   Nimikkeet suuntaa-antavine sisäänostohintoineen. Tarjouksessa
   nimike antaa oletushinnan; projektikohtainen tarkka hinta
   syötetään/tuodaan tarjousriville. Materiaali on kolmannes
   tarjouksesta eikä sitä pidä arvata könttänä.
   ═══════════════════════════════════════════════════════════ */

const RYHMAT = [
  "putki", "kayra", "laippa", "supistaja", "t-kappale", "venttiili",
  "pultti", "mutteri", "tiiviste", "kaasu", "hitsauslisaaine", "muu",
];
/* Ryhmän nimi avaimena: moduulin tasolla t() jäädyttäisi kielen lataushetkeen. */
const RYHMA_AVAIN: Record<string, KaannosAvain> = {
  putki: "mat.r_putki", kayra: "mat.r_kayra", laippa: "mat.r_laippa", supistaja: "mat.r_supistaja",
  "t-kappale": "mat.r_tkappale", venttiili: "mat.r_venttiili", pultti: "mat.r_pultti",
  mutteri: "mat.r_mutteri", tiiviste: "mat.r_tiiviste", kaasu: "mat.r_kaasu",
  hitsauslisaaine: "mat.r_lisaaine", muu: "mat.r_muu",
};
const ryhmaNimi = (r: string): string => (RYHMA_AVAIN[r] ? t(RYHMA_AVAIN[r]!) : r);

const eur = (n: number | null) =>
  n == null ? "—" : n.toLocaleString("fi-FI", { maximumFractionDigits: 2 }) + " €";

export default function Materiaalit({ kayttaja, osoiteHaku, asetaOsoiteHaku }: { kayttaja: Kayttaja; osoiteHaku?: string; asetaOsoiteHaku?: (h: string) => void }) {
  const saaMuokata = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija"].includes(r));
  const [lista, setLista] = useState<Materiaali[] | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [uusiAuki, setUusiAuki] = useState(false);

  // Uusi nimike
  const [nimi, setNimi] = useState("");
  const [ryhma, setRyhma] = useState("putki");
  const [yksikko, setYksikko] = useState("kpl");
  const [dn, setDn] = useState("");
  const [paineluokka, setPaineluokka] = useState("");
  const [laatu, setLaatu] = useState("");
  const [hinta, setHinta] = useState("");

  // Excel-tuonti
  const [tuontiEsikatselu, setTuontiEsikatselu] = useState<MateriaaliTuonti | null>(null);
  const [tuontiSisalto, setTuontiSisalto] = useState("");
  const [tuomassa, setTuomassa] = useState(false);

  async function valitseTiedosto(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setVirhe(null); setTuomassa(true);
    try {
      const b64 = await new Promise<string>((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result));
        r.onerror = () => rej(new Error("luku"));
        r.readAsDataURL(f);
      });
      const tulos = await api.tuoMateriaalit(b64, false);
      if (tulos.virhe) { setVirhe(tulos.viesti ?? t("mat.tuonti_virhe")); setTuontiEsikatselu(null); }
      else { setTuontiEsikatselu(tulos); setTuontiSisalto(b64); }
    } catch { setVirhe(t("mat.luku_virhe")); }
    finally { setTuomassa(false); }
  }
  async function vahvistaTuonti() {
    setTuomassa(true);
    try {
      await api.tuoMateriaalit(tuontiSisalto, true);
      setTuontiEsikatselu(null); setTuontiSisalto(""); setVirhe(null);
      await lataa();
    } catch { setVirhe(t("ltar.tallennus_virhe")); }
    finally { setTuomassa(false); }
  }

  const lataa = useCallback(async () => {
    try { setLista(await api.materiaalit()); setVirhe(null); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("pc.lataus_virhe")); setLista([]); }
  }, []);
  useEffect(() => { void lataa(); }, [lataa]);

  // A3–A7: jaettu lajittelu ja haku; ryhmittely ryhmän mukaan säilyy sen päällä.
  const tb = useTaulukko(lista, {
    avaimet: { nimi: (m) => m.nimi, dn: (m) => m.dn, pn: (m) => m.paineluokka, laatu: (m) => m.laatu, yksikko: (m) => m.yksikko, hinta: (m) => m.sisaostohinta },
    hakukentat: (m) => [m.nimi, m.tunnus, m.laatu, m.paineluokka, m.dn == null ? null : String(m.dn)],
    oletus: "nimi", yksikko: t("mat.yksikko"), osoiteHaku, asetaOsoiteHaku,
  });
  const ryhmitelty = useMemo(() => {
    const m = new Map<string, Materiaali[]>();
    for (const x of lista ?? []) {
      const r = x.ryhma ?? "muu";
      (m.get(r) ?? m.set(r, []).get(r)!).push(x);
    }
    return [...m.entries()].sort((a, b) =>
      RYHMAT.indexOf(a[0]) - RYHMAT.indexOf(b[0]));
  }, [lista]);

  const lisaa = async () => {
    if (!nimi.trim()) { setVirhe(t("mat.anna_nimi")); return; }
    try {
      await api.lisaaMateriaali({
        nimi: nimi.trim(), ryhma, yksikko: yksikko.trim() || "kpl",
        dn: dn.trim() ? Number(dn) : null,
        paineluokka: paineluokka.trim() || null,
        laatu: laatu.trim() || null,
        sisaostohinta: hinta.trim() ? Number(hinta.replace(",", ".")) : null,
      });
      setNimi(""); setDn(""); setPaineluokka(""); setLaatu(""); setHinta(""); setUusiAuki(false); setVirhe(null);
      await lataa();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("qr.lisays_virhe")); }
  };

  const tallennaHinta = async (m: Materiaali, arvo: string) => {
    const h = arvo.trim() === "" ? null : Number(arvo.replace(",", "."));
    if (h === m.sisaostohinta) return;
    try { await api.paivitaMateriaali(m.id, { sisaostohinta: h }); await lataa(); }
    catch { /* näkyy latauksessa */ }
  };

  return (
    <>
    {/* Ohje ylös (Toni 24.9.2026; puuttui 19 näkymän listalta). Lähde: /api/materiaalit*: vaadiRooli(paakayttaja,
        suunnittelija) + rajaamaton + moduuli tarjous. Paneelin selite siirtyi tähän. */}
    <Ohje
      kuka={<>{t("mat.ohje.kuka")}</>}
      luvut={<>{t("mat.ohje.luvut")}</>} />
    <section className="panel">
      <div className="panel__otsikko">
        <div>
          {/* h1 eikä h2: renderöintiportti ja kavely.mjs lukevat näkymän
              nimen h1:stä, ja ilman sitä portti oli tälle näkymälle sokea. */}
          <h1>{t("mat.otsikko")}</h1>
        </div>
        {saaMuokata && (
          <div style={{ display: "flex", gap: 8 }}>
            <a className="nappi nappi--kevyt" href={api.materiaalipohjaUrl()} download
               title={t("mat.pohja_title")}>{t("mat.lataa_pohja")}</a>
            <label className="nappi nappi--kevyt" style={{ cursor: "pointer" }}>
              {t("mat.tuo_excel")}
              <input type="file" accept=".xlsx" style={{ display: "none" }}
                     disabled={tuomassa} onChange={valitseTiedosto} />
            </label>
            <button className="nappi nappi--kevyt" onClick={() => setUusiAuki((v) => !v)}>
              {uusiAuki ? t("hl.peruuta") : t("mat.uusi_nimike")}
            </button>
          </div>
        )}
      </div>

      {virhe && <p className="huom" role="alert">{virhe}</p>}

      {tuontiEsikatselu && (
        <div className="panel" style={{ padding: 12, margin: "8px 0" }}>
          <h3>{t("mat.esikatselu")}</h3>
          <p className="dim">
            <b>{tuontiEsikatselu.tuotavia}</b> {t("mat.tuotavaa")}
            {tuontiEsikatselu.ohitetut ? " · " + t("mat.ohitettu", { n: tuontiEsikatselu.ohitetut }) : ""}
            {" · " + t("mat.tunnistetut") + " "}
            {Object.values(tuontiEsikatselu.mappays ?? {}).join(", ") || "—"}
          </p>
          <table className="selailu__taulu">
            <thead><tr><th>{t("iv.nimi")}</th><th>DN</th><th>PN</th><th>{t("mat.laatu")}</th><th>{t("mat.yks")}</th><th>{t("mat.eur_yks")}</th></tr></thead>
            <tbody>
              {(tuontiEsikatselu.esikatselu ?? []).map((r, i) => (
                <tr key={i}>
                  <td>{r.nimi}</td>
                  <td className="mono">{r.dn ?? ""}</td>
                  <td className="mono">{r.paineluokka ?? ""}</td>
                  <td className="dim">{r.laatu ?? ""}</td>
                  <td className="dim">{r.yksikko ?? ""}</td>
                  <td className="mono">{r.sisaostohinta ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(tuontiEsikatselu.tuotavia ?? 0) > 20 && (
            <p className="dim">{t("mat.naytetaan_20", { n: tuontiEsikatselu.tuotavia ?? 0 })}</p>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button className="nappi nappi--paa" disabled={tuomassa} onClick={() => void vahvistaTuonti()}>
              {t("mat.vahvista_tuonti", { n: tuontiEsikatselu.tuotavia ?? 0 })}
            </button>
            <button className="nappi nappi--kevyt"
                    onClick={() => { setTuontiEsikatselu(null); setTuontiSisalto(""); }}>{t("hl.peruuta")}</button>
          </div>
        </div>
      )}

      {uusiAuki && saaMuokata && (
        <div className="asetukset__uusi">
          <label>{t("iv.nimi")}<input placeholder={esim(t("mat.nimi_esim"))} value={nimi} onChange={(e) => setNimi(e.target.value)} size={20} /></label>
          <label>{t("mat.ryhma_otsikko")}<select value={ryhma} onChange={(e) => setRyhma(e.target.value)}>
            {RYHMAT.map((r) => <option key={r} value={r}>{ryhmaNimi(r)}</option>)}
          </select></label>
          <label>DN<input placeholder={esim("100")} value={dn} onChange={(e) => setDn(e.target.value)} style={{ width: 90 }} /></label>
          <label>PN<input placeholder={esim("16")} value={paineluokka} onChange={(e) => setPaineluokka(e.target.value)} style={{ width: 90 }} /></label>
          <label>{t("mat.laatu")}<input placeholder={esim("1.4404")} value={laatu} onChange={(e) => setLaatu(e.target.value)} size={10} /></label>
          <label>{t("iv.yksikko")}<input placeholder={esim(t("mat.yksikko_esim"))} value={yksikko} onChange={(e) => setYksikko(e.target.value)} style={{ width: 90 }} /></label>
          <label>{t("mat.eur_yks")}<input placeholder={esim("12,50")} value={hinta} onChange={(e) => setHinta(e.target.value)} style={{ width: 100 }} /></label>
          <button className="nappi nappi--paa" onClick={lisaa}>{t("yri.lisaa")}</button>
        </div>
      )}

      {lista === null && <p className="dim">{t("yleinen.ladataan")}</p>}
      {lista && lista.length === 0 && <p className="dim">{t("mat.tyhja")}</p>}
      {lista && lista.length > 0 && (
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
          {tb.Haku({ placeholder: t("mat.haku") })}
          {tb.piilotettu > 0 && <span className="dim">{t("kal.piilossa", { n: tb.piilotettu })}</span>}
        </div>
      )}

      {ryhmitelty.map(([r, rivit]) => (
        <div key={r} className="materiaali__ryhma">
          <h3>{ryhmaNimi(r)}</h3>
          <table className="selailu__taulu materiaali__taulu">
            <thead>
              <tr><th>{tb.Otsikko("nimi", t("iv.nimi"))}</th><th>{tb.Otsikko("dn", "DN")}</th><th>{tb.Otsikko("pn", "PN")}</th><th>{tb.Otsikko("laatu", t("mat.laatu"))}</th><th>{tb.Otsikko("yksikko", t("mat.yks"))}</th><th>{tb.Otsikko("hinta", t("mat.eur_yks"))}</th><th></th></tr>
            </thead>
            <tbody>
              {rivit.map((m) => (
                <tr key={m.id}>
                  <td>{m.nimi}</td>
                  <td className="mono">{m.dn ?? ""}</td>
                  <td className="mono">{m.paineluokka ?? ""}</td>
                  <td className="dim">{m.laatu ?? ""}</td>
                  <td className="dim">{m.yksikko}</td>
                  <td className="asetukset__syote">
                    {saaMuokata
                      ? <input className="lukukentta__syote" style={{ width: 80 }}
                               defaultValue={m.sisaostohinta ?? ""} inputMode="decimal"
                               onBlur={(e) => void tallennaHinta(m, e.target.value)} />
                      : <span className="mono">{eur(m.sisaostohinta)}</span>}
                  </td>
                  <td>
                    {saaMuokata && (
                      <button className="asetukset__poista" title={t("mat.passivoi")}
                              onClick={async () => { await api.poistaMateriaali(m.id); await lataa(); }}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>
    </>
  );
}
